import React from 'react';

import { useTheme } from 'styled-components';

import { IoIosCloseCircle } from 'react-icons/io';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';

import { exportCsv } from '~/helpers/csv';
import { exportPdf } from '~/helpers/pdf';

import { Content, Count, Options, Summary } from './styles';

import { ParamsProps } from './interface';
import { toNumber } from '~/helpers/number';

const Agrupamento: React.FC<ParamsProps> = ({
  pedidosSelecionados,
  dismissAll,
}) => {
  const { colors } = useTheme();

  const gerarRelatorioPedidosCSV = () => {
    let data: string[] = [];

    data.push(
      'IDPEDIDO;TID;INTEGRADO;FATURADO;DATA;CLIENTE;STATUS;SITUAÇÃO;VALOR',
    );

    let c;
    for (c in pedidosSelecionados) {
      const pedido = pedidosSelecionados[c];
      data.push(
        `${pedido.idpedido};${pedido.tiderp};${
          pedido.tiderp !== '' ? 'SIM' : 'NÃO'
        };${pedido.numnfe !== '' ? 'SIM' : 'NÃO'};${pedido.dt};${
          pedido.cliente.nome
        };${pedido.status};${pedido.situacao};${pedido.precototal}`,
      );
    }

    const csv = data.join('\n');

    exportCsv(csv, 'RELAÇÃO DE PEDIDOS');
  };

  const gerarRelatorioPedidosPDF = () => {
    const cabecalho = [
      'IDPEDIDO',
      'TID',
      'INTEGRADO',
      'FATURADO',
      'DATA',
      'CLIENTE',
      'STATUS',
      'SITUACAO',
      'VALOR',
    ];

    const data: string[][] = [];

    let c;
    for (c in pedidosSelecionados) {
      const pedido = pedidosSelecionados[c];
      const linha: string[] = [];

      linha.push(pedido.idpedido);
      linha.push(pedido.tiderp);
      linha.push(`${pedido.tiderp !== '' ? 'SIM' : 'NÃO'}`);
      linha.push(`${pedido.numnfe !== '' ? 'SIM' : 'NÃO'}`);
      linha.push(pedido.dt);
      linha.push(pedido.cliente.nome);
      linha.push(pedido.status);
      linha.push(pedido.situacao);
      linha.push(pedido.precototalformatado);

      data.push(linha);
    }

    exportPdf(cabecalho, data, 'RELAÇÃO DE PEDIDOS');
  };

  const gerarRelatorioProdutosCSV = () => {
    let data: string[] = [];

    data.push('SKU;DESCRICAO;VALORUNIT;QUANTIDADE;');

    let itens: Array<{
      sku: string;
      descricao: string;
      valorunit: string;
      quantidade: string;
    }> = [];

    let c;
    for (c in pedidosSelecionados) {
      const pedido = pedidosSelecionados[c];
      if (pedido.itens) {
        let t;
        for (t in pedido.itens) {
          const item = pedido.itens[t];

          let existe = false;

          itens = itens.map(cItem => {
            if (cItem.sku === item.sku) {
              existe = false;

              return {
                ...cItem,
                quantidade: cItem.quantidade + item.qtd,
              };
            }

            return cItem;
          });

          if (!existe) {
            itens.push({
              sku: `${item.sku}`,
              descricao: `${item.descricao}`,
              valorunit: `${item.precounit}`,
              quantidade: item.qtd,
            });
          }
        }
      }
    }

    let i;
    for (i in itens) {
      const item = itens[i];
      data.push(
        `${item.sku};${item.descricao};${item.valorunit};${item.quantidade}`,
      );
    }

    const csv = data.join('\n');

    exportCsv(csv, 'RELAÇÃO DE PRODUTOS');
  };

  const gerarRelatorioProdutosPDF = () => {
    const cabecalho = ['SKU', 'DESCRICAO', 'VALORUNIT', 'QUANTIDADE'];

    let itens: Array<{
      sku: string;
      descricao: string;
      valorunit: string;
      quantidade: string;
    }> = [];

    let c;
    for (c in pedidosSelecionados) {
      const pedido = pedidosSelecionados[c];
      if (pedido.itens) {
        let t;
        for (t in pedido.itens) {
          const item = pedido.itens[t];

          let existe = false;

          itens = itens.map(cItem => {
            if (cItem.sku === item.sku) {
              existe = false;

              return {
                ...cItem,
                quantidade: cItem.quantidade + item.qtd,
              };
            }

            return cItem;
          });

          if (!existe) {
            itens.push({
              sku: `${item.sku}`,
              descricao: `${item.descricao}`,
              valorunit: `${item.precounit}`,
              quantidade: item.qtd,
            });
          }
        }
      }
    }

    const data: string[][] = [];

    let i;
    for (i in itens) {
      const item = itens[i];

      const linha: string[] = [];

      linha.push(item.sku);
      linha.push(item.descricao);
      linha.push(item.valorunit);
      linha.push(item.quantidade);

      data.push(linha);
    }

    exportPdf(cabecalho, data, 'RELAÇÃO DE PRODUTOS');
  };

  const resumo = (modulo: string) => {
    switch (modulo) {
      case 'FATURAMENTO':
        return `${pedidosSelecionados
          .reduce((acc, pedido) => {
            acc += toNumber(pedido.precototal);
            return acc;
          }, 0)
          .toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`;

      case 'FRETE':
        return `${pedidosSelecionados
          .reduce((acc, pedido) => {
            acc += toNumber(pedido.precofrete);
            return acc;
          }, 0)
          .toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`;

      case 'DESCONTO':
        return `${pedidosSelecionados
          .reduce((acc, pedido) => {
            acc += toNumber(pedido.precodesconto);
            return acc;
          }, 0)
          .toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`;

      case 'TAXAS':
        return `${pedidosSelecionados
          .reduce((acc, pedido) => {
            acc += toNumber(pedido.precotaxas);
            return acc;
          }, 0)
          .toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`;

      case 'PRODUTOS':
        return `${pedidosSelecionados.reduce((acc, pedido) => {
          acc += pedido.itens.length;
          return acc;
        }, 0)}`;

      case 'ITENS':
        return `${pedidosSelecionados.reduce((acc, pedido) => {
          let i;
          for (i in pedido.itens) {
            const item = pedido.itens[i];
            acc += toNumber(item.qtd);
          }
          return acc;
        }, 0)}`;
    }
  };

  return (
    <Content>
      <Count>
        <button onClick={dismissAll}>
          <IoIosCloseCircle size={26} color={colors.IC1} />
        </button>
        <span>
          {pedidosSelecionados.length > 1
            ? `${pedidosSelecionados.length} pedidos selecionados`
            : 'Um pedido selecionado'}
        </span>
      </Count>

      <Summary>
        <div>
          <span style={{ color: '#05df72' }}>FATURAMENTO:</span>
          <strong style={{ color: '#05df72' }}>{resumo('FATURAMENTO')}</strong>
        </div>
        <div>
          <span>QUANTIDADE DE PRODUTOS:</span>
          <strong>{resumo('PRODUTOS')}</strong>
        </div>
        <div>
          <span>QUANTIDADE DE ITENS:</span>
          <strong>{resumo('ITENS')}</strong>
        </div>
        <div>
          <span style={{ color: '#ff8904' }}>TOTAL EM DESCONTOS:</span>
          <strong style={{ color: '#ff8904' }}>{resumo('DESCONTO')}</strong>
        </div>
        <div>
          <span style={{ color: '#bc98dd' }}>TOTAL EM TAXAS:</span>
          <strong style={{ color: '#bc98dd' }}>{resumo('TAXAS')}</strong>
        </div>
        <div>
          <span style={{ color: '#51a2ff' }}>TOTAL EM FRETE:</span>
          <strong style={{ color: '#51a2ff' }}>{resumo('FRETE')}</strong>
        </div>
      </Summary>

      <Options>
        <button
          className="excel"
          onClick={gerarRelatorioPedidosCSV}
          title="Exportar para Excel"
        >
          <FaFileExcel size={22} color={colors.IC2} />
          <div>
            <h3>EXPORTAR</h3>
            <h2>RELAÇÃO DE PEDIDOS</h2>
          </div>
        </button>
        <button
          className="pdf"
          onClick={gerarRelatorioPedidosPDF}
          title="Exportar para PDF"
        >
          <FaFilePdf size={22} color={colors.IC2} />
          <div>
            <h3>EXPORTAR</h3>
            <h2>RELAÇÃO DE PEDIDOS</h2>
          </div>
        </button>

        <button
          className="excel"
          onClick={gerarRelatorioProdutosCSV}
          title="Exportar para Excel"
        >
          <FaFileExcel size={22} color={colors.IC2} />
          <div>
            <h3>EXPORTAR</h3>
            <h2>RELAÇÃO DE PRODUTOS</h2>
          </div>
        </button>
        <button
          className="pdf"
          onClick={gerarRelatorioProdutosPDF}
          title="Exportar para PDF"
        >
          <FaFilePdf size={22} color={colors.IC2} />
          <div>
            <h3>EXPORTAR</h3>
            <h2>RELAÇÃO DE PRODUTOS</h2>
          </div>
        </button>
      </Options>
    </Content>
  );
};

export default Agrupamento;
